import { LitElement, html, nothing, type CSSResultGroup } from 'lit';
import { property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import type { DropdownChangeEventDetail } from '../types.js';
import styles from './styles.js';

/** Detail payload for uwc-page-change */
export interface PageChangeDetail {
  first:     number;
  rows:      number;
  page:      number;   // 0-based
  pageCount: number;
}

/**
 * uwc-paginator
 *
 * Full-featured paginator component.
 *
 * @attr first                      - Index of the first record. Default: 0.
 * @attr rows                       - Rows displayed per page. Default: 10.
 * @attr total-records              - Total number of records. Default: 0.
 * @attr page-links                 - Max page link buttons to show. Default: 5.
 * @attr rows-per-page-options      - Comma-separated list of rows-per-page options, e.g. "5,10,20".
 * @attr show-first-last            - Show first/last nav buttons. Default: true.
 * @attr show-page-links            - Show page number buttons. Default: true.
 * @attr show-current-page-report   - Show current page report text. Default: false.
 * @attr current-page-report-template - Template. Tokens: {currentPage} {totalPages} {first} {last} {totalRecords}. Default: '({currentPage} of {totalPages})'.
 * @attr show-jump-to-page-input    - Show numeric jump-to-page input. Default: false.
 * @attr show-jump-to-page-dropdown - Show jump-to-page dropdown. Default: false.
 * @attr always-show                - Always render even when there is only one page. Default: true.
 * @attr disabled                   - Disable all controls.
 *
 * @slot start - Content rendered at the left of the paginator.
 * @slot end   - Content rendered at the right of the paginator.
 *
 * @fires uwc-page-change - Fired when the page or rows-per-page changes.
 *                          Detail: { first, rows, page, pageCount }.
 *
 * @csspart container        - The outer wrapper div.
 * @csspart nav-first        - First-page button.
 * @csspart nav-prev         - Prev-page button.
 * @csspart nav-next         - Next-page button.
 * @csspart nav-last         - Last-page button.
 * @csspart page-btn         - A page-number button.
 * @csspart page-btn-active  - The currently-active page-number button.
 * @csspart rows-per-page    - Rows-per-page select.
 * @csspart report           - Current page report span.
 * @csspart jump-input       - Jump-to-page text input.
 * @csspart jump-dropdown    - Jump-to-page select.
 *
 * @cssprop --uwc-paginator-padding         - Wrapper padding. Default: 0.5rem 1rem.
 * @cssprop --uwc-paginator-bg              - Background. Default: surface token.
 * @cssprop --uwc-paginator-border          - Top border. Default: 1px solid rgba(0,0,0,0.08).
 * @cssprop --uwc-paginator-radius          - Border radius. Default: radiusMd token.
 * @cssprop --uwc-paginator-font-size       - Font size. Default: fontSizeMd token.
 * @cssprop --uwc-paginator-btn-radius      - Jump-to-page input border radius.
 * @cssprop --uwc-paginator-label-size      - Label font size. Default: 0.8125rem.
 *
 * Nav and page buttons are <uwc-button size="small"> — theme them via the
 * button's own tokens/variants (e.g. --uwc-color-primary for the active page,
 * --uwc-color-secondary for nav/inactive page colour) so they stay consistent
 * with the other form controls and rescale with the Theme Designer.
 */
export class UwcPaginator extends LitElement {

  static override styles: CSSResultGroup = styles as CSSResultGroup;

  // ── Core ──────────────────────────────────────────────────────────────

  @property({ type: Number, reflect: true })               first           = 0;
  @property({ type: Number, reflect: true })               rows            = 10;
  @property({ type: Number, attribute: 'total-records' })  totalRecords    = 0;
  @property({ type: Number, attribute: 'page-links' })     pageLinks       = 5;

  // ── Feature flags ─────────────────────────────────────────────────────

  @property({ attribute: 'rows-per-page-options' })        rowsPerPageOptions: string | null = null;
  @property({ type: Boolean, attribute: 'show-first-last' })         showFirstLast          = true;
  @property({ type: Boolean, attribute: 'show-page-links' })         showPageLinks          = true;
  @property({ type: Boolean, attribute: 'show-current-page-report' }) showCurrentPageReport = false;
  @property({ attribute: 'current-page-report-template' }) currentPageReportTemplate        = '({currentPage} of {totalPages})';
  @property({ type: Boolean, attribute: 'show-jump-to-page-input' })    showJumpToPageInput    = false;
  @property({ type: Boolean, attribute: 'show-jump-to-page-dropdown' }) showJumpToPageDropdown = false;
  @property({ type: Boolean, reflect: true })              alwaysShow      = true;
  @property({ type: Boolean, reflect: true })              disabled        = false;

  // ── Internal ─────────────────────────────────────────────────────────

  @state() private _jumpValue = '';

  // ── Computed helpers ──────────────────────────────────────────────────

  private get _pageCount() {
    return this.rows > 0 ? Math.max(1, Math.ceil(this.totalRecords / this.rows)) : 1;
  }

  private get _currentPage() {
    return this.rows > 0 ? Math.floor(this.first / this.rows) : 0;
  }

  private get _rppOptions(): number[] {
    if (!this.rowsPerPageOptions) return [];
    return this.rowsPerPageOptions.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
  }

  /** Build the sliding-window page number array with -1 as ellipsis sentinel */
  private _buildPageLinks(): number[] {
    const pc    = this._pageCount;
    const cur   = this._currentPage;
    const max   = this.pageLinks;

    if (pc <= max) {
      return Array.from({ length: pc }, (_, i) => i);
    }

    const half  = Math.floor(max / 2);
    let start   = cur - half;
    let end     = cur + half;

    if (start < 0) { end += -start; start = 0; }
    if (end >= pc) { start -= end - pc + 1; end = pc - 1; }
    start = Math.max(0, start);

    const pages: number[] = [];
    if (start > 0) {
      pages.push(0);
      if (start > 1) pages.push(-1); // left ellipsis
    }
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < pc - 1) {
      if (end < pc - 2) pages.push(-1); // right ellipsis
      pages.push(pc - 1);
    }
    return pages;
  }

  // ── Navigation helpers ────────────────────────────────────────────────

  private _goToPage(page: number) {
    if (this.disabled) return;
    const pc = this._pageCount;
    page = Math.max(0, Math.min(page, pc - 1));
    const newFirst = page * this.rows;
    if (newFirst === this.first) return;
    this.first = newFirst;
    this._emit();
  }

  private _setRows(rows: number) {
    if (this.disabled) return;
    this.rows  = rows;
    this.first = 0;
    this._emit();
  }

  private _emit() {
    const detail: PageChangeDetail = {
      first:     this.first,
      rows:      this.rows,
      page:      this._currentPage,
      pageCount: this._pageCount,
    };
    this.dispatchEvent(new CustomEvent('uwc-page-change', {
      bubbles: true, composed: true, detail,
    }));
  }

  // ── Jump to page ──────────────────────────────────────────────────────

  private _onJumpInput(e: InputEvent) {
    this._jumpValue = (e.target as HTMLInputElement).value;
  }

  private _onJumpKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') this._commitJump();
  }

  private _onJumpBlur() {
    this._commitJump();
  }

  private _commitJump() {
    const page = parseInt(this._jumpValue, 10);
    if (!isNaN(page)) {
      this._goToPage(page - 1); // 1-based input → 0-based
    }
    this._jumpValue = '';
  }

  private _onJumpDropdownChange(e: CustomEvent<DropdownChangeEventDetail>) {
    const page = Number(e.detail.value);
    if (!isNaN(page)) this._goToPage(page);
  }

  private _onRppChange(e: CustomEvent<DropdownChangeEventDetail>) {
    const rows = Number(e.detail.value);
    if (!isNaN(rows)) this._setRows(rows);
  }

  // ── Render ────────────────────────────────────────────────────────────

  override render() {
    const pc  = this._pageCount;
    const cur = this._currentPage;

    if (!this.alwaysShow && pc <= 1) return nothing;

    const isFirst = cur === 0;
    const isLast  = cur >= pc - 1;

    // ── Rows per page ──────────────────────────────────────────────────
    const rppTpl = this._rppOptions.length > 0 ? html`
      <span class="uwc-pg__rpp">
        <span class="uwc-pg__rpp-label">Rows per page:</span>
        <uwc-dropdown
          part="rows-per-page"
          class="uwc-pg__rpp-select"
          .options=${this._rppOptions.map(n => ({ label: String(n), value: n }))}
          .value=${this.rows}
          ?disabled=${this.disabled}
          @uwc-change=${this._onRppChange}
        ></uwc-dropdown>
      </span>
    ` : nothing;

    // ── Current page report ────────────────────────────────────────────
    const reportTpl = this.showCurrentPageReport ? html`
      <span part="report" class="uwc-pg__report">
        ${this._buildReport(cur, pc)}
      </span>
    ` : nothing;

    // ── Jump to page input ─────────────────────────────────────────────
    const jumpInputTpl = this.showJumpToPageInput ? html`
      <span class="uwc-pg__jump">
        <span class="uwc-pg__jump-label">Go to:</span>
        <input
          part="jump-input"
          class="uwc-pg__jump-input"
          type="number"
          min="1"
          max=${pc}
          .value=${this._jumpValue}
          ?disabled=${this.disabled}
          @input=${this._onJumpInput}
          @keydown=${this._onJumpKeydown}
          @blur=${this._onJumpBlur}
        />
      </span>
    ` : nothing;

    // ── Jump to page dropdown ──────────────────────────────────────────
    const jumpDropdownTpl = this.showJumpToPageDropdown ? html`
      <span class="uwc-pg__jump">
        <span class="uwc-pg__jump-label">Go to:</span>
        <uwc-dropdown
          part="jump-dropdown"
          class="uwc-pg__jump-select"
          .options=${Array.from({ length: pc }, (_, i) => ({ label: String(i + 1), value: i }))}
          .value=${cur}
          ?disabled=${this.disabled}
          @uwc-change=${this._onJumpDropdownChange}
        ></uwc-dropdown>
      </span>
    ` : nothing;

    // ── Page links ─────────────────────────────────────────────────────
    const pageLinksTpl = this.showPageLinks ? html`
      <span class="uwc-pg__group">
        ${repeat(this._buildPageLinks(), (p, idx) => `${p}-${idx}`, (p) => {
          if (p === -1) return html`<span class="uwc-pg__ellipsis">&hellip;</span>`;
          const active = p === cur;
          return html`
            <uwc-button
              part=${active ? 'page-btn page-btn-active' : 'page-btn'}
              class="uwc-pg__page"
              variant=${active ? 'primary' : 'secondary'}
              ?text=${!active}
              ?disabled=${this.disabled}
              aria-current=${active ? 'page' : nothing}
              aria-label="Page ${p + 1}"
              @uwc-click=${() => this._goToPage(p)}
            >${p + 1}</uwc-button>
          `;
        })}
      </span>
    ` : nothing;

    return html`
      <div part="container" class="uwc-pg" role="navigation" aria-label="Pagination">

        <slot name="start" class="uwc-pg__slot-start"></slot>

        ${rppTpl}

        ${reportTpl}

        <span class="uwc-pg__group">
          ${this.showFirstLast ? html`
            <uwc-button
              part="nav-first"
              class="uwc-pg__nav"
              text
              variant="secondary"
              icon-only
              icon="chevron-bar-left"
              ?disabled=${isFirst || this.disabled}
              aria-label="First page"
              @uwc-click=${() => this._goToPage(0)}
            ></uwc-button>
          ` : nothing}

          <uwc-button
            part="nav-prev"
            class="uwc-pg__nav"
            text
            variant="secondary"
            icon-only
            icon="chevron-left"
            ?disabled=${isFirst || this.disabled}
            aria-label="Previous page"
            @uwc-click=${() => this._goToPage(cur - 1)}
          ></uwc-button>
        </span>

        ${pageLinksTpl}

        <span class="uwc-pg__group">
          <uwc-button
            part="nav-next"
            class="uwc-pg__nav"
            text
            variant="secondary"
            icon-only
            icon="chevron-right"
            ?disabled=${isLast || this.disabled}
            aria-label="Next page"
            @uwc-click=${() => this._goToPage(cur + 1)}
          ></uwc-button>

          ${this.showFirstLast ? html`
            <uwc-button
              part="nav-last"
              class="uwc-pg__nav"
              text
              variant="secondary"
              icon-only
              icon="chevron-bar-right"
              ?disabled=${isLast || this.disabled}
              aria-label="Last page"
              @uwc-click=${() => this._goToPage(pc - 1)}
            ></uwc-button>
          ` : nothing}
        </span>

        ${jumpInputTpl}
        ${jumpDropdownTpl}

        <slot name="end" class="uwc-pg__slot-end"></slot>
      </div>
    `;
  }

  private _buildReport(cur: number, pc: number): string {
    const first = this.first + 1;
    const last  = Math.min(this.first + this.rows, this.totalRecords);
    return this.currentPageReportTemplate
      .replace('{currentPage}',  String(cur + 1))
      .replace('{totalPages}',   String(pc))
      .replace('{first}',        String(first))
      .replace('{last}',         String(last))
      .replace('{totalRecords}', String(this.totalRecords));
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-paginator': UwcPaginator; }
}
